import React, {FC, useState, useEffect} from "react"
import {isValidPhoneNumber, parsePhoneNumber} from "react-phone-number-input"

import {Body} from "@irati/ui/src/Typography"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import {IconTypes} from "@irati/ui/src/Button"
import authentication from "@irati/auth"
import {PhoneInput, ValidationButton} from "@irati/ui/src/FormItem"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import Form from "@irati/ui/src/Form"
import Validation from "@irati/utils/validation"
import Icon from "@irati/ui/src/Icon"
import {useBranding} from "@irati/brand"
import tracking, {LoginType} from "@irati-tracking/platform/platform"

import {BusinessLogo, PhoneImg, StyledButton} from "./styled"

interface Props {
  load: Promise<any> | null
  error: string
  phoneNo?: string
  onClick: (email: string) => void
  skipPhoneNo: () => void
}

const VerifyPhone: FC<Props> = ({error, load, phoneNo, onClick, skipPhoneNo}) => {
  const [phone, setPhone] = useState(phoneNo || "")
  const [phoneError, setPhoneError] = useState<string | null>(null)

  const {formatMessage} = useIntl()
  const branding = useBranding()

  const handleOnInputChange = (value) => {
    setPhoneError("")
    setPhone(value)
  }

  const handleOnClick = () => {
    if (phone && isValidPhoneNumber(phone)) {
      tracking.loginOTPSubmit("phone_number", LoginType.phoneCapture, undefined, undefined, phone)
      const parsedPhoneNumber = parsePhoneNumber(phone)?.number || phone
      onClick(parsedPhoneNumber)
    }
  }

  useEffect(() => {
    localStorage.setItem("login_type", LoginType.phoneCapture)
    tracking.phoneCaptureViewPage("phone_number_capture")
    tracking.loginViewPage("phone_number_capture")
    if (authentication.user().phoneNumber) {
      setPhone(authentication.user().phoneNumber!)
    }
  }, [])

  useEffect(() => {
    setPhoneError(error)
  }, [error])

  return (
    <>
      <Flex justifyContent="flex-end" marginBottom={2}>
        <StyledButton small tertiary onClick={skipPhoneNo}>
          <FormattedMessage id="platform.oneTimePassword.enterPhone.skip" />
          <Icon icon={IconTypes.angleRight} small marginLeft={0.5} size={20} />
        </StyledButton>
      </Flex>
      <Flex direction="column" alignItems="center">
        {!!phoneError && (
          <Alert
            type={AlertTypes.error}
            title={phoneError}
            subTitle="platform.oneTimePassword.enterEmail.error.text"
            onHide={() => setPhoneError(null)}
          />
        )}
        {branding?.businessLogoUrl && <BusinessLogo src={formatMessage({id: branding?.businessLogoUrl})} />}
        <PhoneImg src={formatMessage({id: "platform.oneTimePassword.enterPhone.img"})} />
        <Body small weight="semibold" marginBottom={0.5} maxWidth="100%" textAlign="center">
          <FormattedMessage id="platform.oneTimePassword.enterPhone.subTitle1" />
        </Body>
        <Body small weight="light" marginBottom={2} maxWidth="100%" textAlign="center">
          <FormattedMessage id="platform.oneTimePassword.enterPhone.subTitle2" />
        </Body>
        <Flex direction="column" width="100%">
          <Form>
            <PhoneInput
              value={phone}
              validation={Validation.string(formatMessage({id: "platform.mfa.phoneNumber.errors.isRequired"}))}
              label="Enter phone number"
              placeholder="Phone number"
              onChange={handleOnInputChange}
            />
            <ValidationButton load={load} mobileFull full primary onClick={handleOnClick}>
              <FormattedMessage id="platform.oneTimePassword.enterEmail.mobileBtn" />
            </ValidationButton>
          </Form>
        </Flex>
      </Flex>
    </>
  )
}

export default VerifyPhone
